import type { Control, FieldPath, UseFormRegister } from "react-hook-form";

import { motion } from "framer-motion";
import { X } from "lucide-react";
import { z } from "zod";

import type { Step } from "@/hooks/use-multi-step";

import { useCities, useCountries, useStates } from "@/api/util";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMultiStepForm } from "@/hooks/use-multi-step";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/form")({
	component: RouteComponent
});

export const FormDataSchema = z.object({
	businessName: z.string().min(1, "Business name is required"),
	businessPhone: z.string().min(1, "Business phone is required"),

	businessZip: z.string().min(1, "Business zip is required"),
	businessCity: z.string().min(1, "Business city is required"),
	businessState: z.string().min(1, "Business state is required"),
	businessStreet: z.string().min(1, "Business street is required"),

	country: z.string().min(1, "Country is required"),
	street: z.string().min(1, "Street is required"),
	city: z.string().min(1, "City is required"),
	state: z.string().min(1, "State is required"),
	zip: z.string().min(1, "Zip is required")
});

const steps: Step<typeof FormDataSchema>[] = [
	{
		id: "Step 1",
		name: "Business Information",
		fields: ["businessName", "businessPhone"] as const
	},
	{
		id: "Step 2",
		name: "Address",
		fields: ["country", "state", "city", "street", "zip"] as const
	},
	{ id: "Step 3", name: "Complete" }
];

export default function RouteComponent() {
	const form = useMultiStepForm(
		{ resolver: zodResolver(FormDataSchema) },
		{ schema: FormDataSchema, steps },
		processForm
	);

	const delta = form.currentStep - form.previousStep;
	function processForm(_data: z.infer<typeof FormDataSchema>) {
		form.reset();
	}

	const countries = useCountries();

	const { data: states } = useStates(form.watch("country"));

	const { data: cities } = useCities({
		stateId: form.watch("state"),
		countryId: form.watch("country")
	});

	return (
		<section className="absolute inset-0 flex flex-col justify-between p-24 max-sm:p-8">

			<Button variant="ghost" className="absolute top-4 right-4">
				<X className="size-4" />
				<span className="sr-only">Close Form</span>
			</Button>
			<nav aria-label="Progress">
				<ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">

					{steps.map((step, index) => (
						<li key={step.name} className="md:flex-1">
							{form.currentStep > index
								? (
										<div className="group flex w-full flex-col border-l-4 border-primary py-2 pl-4 transition-colors md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
											<span className="text-sm font-medium text-primary transition-colors ">
												{step.id}
											</span>
											<span className="text-sm font-medium">{step.name}</span>
										</div>
									)
								: form.currentStep === index
									? (
											<div
												className="flex w-full flex-col border-l-4 border-primary py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
												aria-current="step"
											>
												<span className="text-sm font-medium text-primary">
													{step.id}
												</span>
												<span className="text-sm font-medium">{step.name}</span>
											</div>
										)
									: (
											<div className="group flex w-full flex-col border-l-4 border-gray-200 py-2 pl-4 transition-colors md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
												<span className="text-sm font-medium text-gray-500 transition-colors">
													{step.id}
												</span>
												<span className="text-sm font-medium">{step.name}</span>
											</div>
										)}
						</li>
					))}
				</ol>
			</nav>

			{/* Form */}
			<Form {...form}>
				<form className="mt-12 py-12" onSubmit={form.handleSubmit(processForm)}>
					{form.currentStep === 0 && (
						<motion.div
							initial={{ x: delta >= 0 ? "50%" : "-50%", opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							transition={{ duration: 0.3, ease: "easeInOut" }}
						>
							<h2 className="text-base font-semibold leading-7 text-gray-900">
								Business Information
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Provide your Business details.
							</p>
							<div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
								<div className="sm:col-span-3">
									<FormField
										control={form.control}
										name="businessName"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessName"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business name
												</FormLabel>
												<FormControl>

													<Input
														id="businessName"
														autoComplete="given-name"
														className="block w-full"
														{...field}
														{...form.register("businessName")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="sm:col-span-3">
									<FormField
										control={form.control}
										name="businessPhone"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessPhone"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business Phone
												</FormLabel>
												<FormControl>

													<Input
														id="businessPhone"
														autoComplete="given-name"
														className="block w-full"
														{...field}
														{...form.register("businessPhone")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

							</div>
						</motion.div>
					)}

					{form.currentStep === 1 && (
						<motion.div
							initial={{ x: delta >= 0 ? "50%" : "-50%", opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							transition={{ duration: 0.3, ease: "easeInOut" }}
						>
							<h2 className="text-base font-semibold leading-7 text-gray-900">
								Address
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Kindly enter an accurate and current address.
							</p>

							<div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

								<div className="col-span-full">
									<FormField
										control={form.control}
										name="businessStreet"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessStreet"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business Street address
												</FormLabel>
												<FormControl>

													<Input
														type="text"
														id="businessStreet"
														className="block w-full"
														{...field}
														{...form.register("businessStreet")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-full">

									<SelectFormField
										control={form.control}
										data={countries.data?.data ?? []}
										label="Country"
										name="country"
										placeholder="Select a country"
										register={form.register}
									/>
								</div>

								<div className="sm:col-span-2 sm:col-start-1">
									<SelectFormField
										control={form.control}
										data={states ?? []}
										label="State / Province"
										name="state"
										placeholder="Select a state"
										register={form.register}
									/>
								</div>
								<div className="sm:col-span-2">
									<SelectFormField
										control={form.control}
										data={cities ?? []}
										label="City"
										name="city"
										placeholder="Select a City"
										register={form.register}
									/>
								</div>

								<div className="sm:col-span-2">
									<label
										htmlFor="zip"
										className="block text-sm font-medium leading-6 text-gray-900"
									>
										ZIP / Postal code
									</label>
									<div className="mt-2">
										<input
											type="text"
											id="zip"
											{...form.register("zip")}
											autoComplete="postal-code"
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6"
										/>
										{form.formState.errors.zip?.message && (
											<p className="mt-2 text-sm text-red-400">
												{form.formState.errors.zip.message}
											</p>
										)}
									</div>
								</div>
							</div>
						</motion.div>
					)}

					{form.currentStep === 2 && (
						<>
							<h2 className="text-base font-semibold leading-7 text-gray-900">
								Complete
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Thank you for your submission.
							</p>
						</>
					)}
				</form>
			</Form>

			{/* Navigation */}
			<div className="mt-8 pt-5">
				<div className="flex justify-between">
					<button
						type="button"
						onClick={form.prev}
						disabled={form.currentStep === 0}
						className="rounded bg-white px-2 py-1 text-sm font-semibold text-sky-900 shadow-sm ring-1 ring-inset ring-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							className="h-6 w-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15.75 19.5L8.25 12l7.5-7.5"
							/>
						</svg>
					</button>
					<button
						type="button"
						onClick={form.next}
						disabled={form.currentStep === steps.length - 1}
						className="rounded bg-white px-2 py-1 text-sm font-semibold text-sky-900 shadow-sm ring-1 ring-inset ring-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							className="h-6 w-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M8.25 4.5l7.5 7.5-7.5 7.5"
							/>
						</svg>
					</button>
				</div>
			</div>
		</section>
	);
}

type TFormSchema = z.infer<typeof FormDataSchema>;

interface SelectFormFieldProps<T extends { id: string; name: string }> {
	data: T[];
	label: string;
	disabled?: boolean;
	placeholder?: string;
	name: FieldPath<TFormSchema>;
	control: Control<TFormSchema>;
	register: UseFormRegister<TFormSchema>;
}

function SelectFormField<T extends { id: string; name: string }>({
	data,
	label,
	name,
	control,
	register,
	disabled,
	placeholder = "Select"
}:
SelectFormFieldProps<T>) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormLabel
						htmlFor={name}
						className="block text-sm font-medium leading-6 text-gray-900"
					>
						{label}
					</FormLabel>
					<FormControl>

						<Select
							disabled={disabled || !data.length}
							onValueChange={field.onChange}
						>
							{/* This (input) is a hack to display the proper error message */}
							<input className="hidden" {...register(name)} />
							<SelectTrigger>
								<SelectValue placeholder={placeholder} {...field} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>

									{data.map(item => (
										<SelectItem key={item.id} value={item.id}>
											{item.name}
										</SelectItem>
									))}

								</SelectGroup>
							</SelectContent>
						</Select>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}
